import { NextContentfulImage } from "@components/Image";
import LinkCopiedBadge from "@components/LinkCopiedBadge/LinkCopiedBadge";
import { SocialLink } from "@customTypes/SocialLink";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import styles from "./C15_SocialSharing.module.scss";

export interface SocialSharingProps {
  socialLinks: SocialLink[];
  isSocialIconDark?: boolean;
  enableSaveToProfileLink?: boolean;
  small?: boolean;
  mobileAlignment?: boolean;
  customClass?: string;
}

export const SocialSharing = (props: SocialSharingProps) => {
  const {
    socialLinks,
    isSocialIconDark,
    small,
    mobileAlignment,
    customClass = "",
  } = props;
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyPing, setCopyPing] = useState(0);
  const { route } = useRouter();

  const replace = (shareLink) =>
    shareLink?.replace("%URL%", encodeURIComponent(url));

  useEffect(() => {
    const url = document.URL;
    setUrl(url);
  }, [route]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 3000);
    return () => window.clearTimeout(id);
  }, [copyPing, copied]);

  const device = useDevice();
  const isMobile = device === "mobile";

  return (
    <div
      className={clsx(
        "component",
        styles["social-sharing"],
        small && styles.small,
        mobileAlignment && styles.mobile,
        customClass
      )}
    >
      <ul className={styles["social-links"]}>
        {socialLinks.map(
          ({ fields: { name, lightIcon, darkIcon, shareLink } }, index) => {
            const icon = isSocialIconDark ? darkIcon : lightIcon;

            return (
              <li key={`${name}${index}`} className={styles.copyItem}>
                {shareLink === "COPY" ? (
                  <>
                    <button
                      type="button"
                      aria-label={`${name} - Copied Link to Clipboard`}
                      rel="noreferrer"
                      className={styles.icon}
                      onClick={() => {
                        navigator.clipboard.writeText(url);
                        setCopied(true);
                        setCopyPing((p) => p + 1);
                      }}
                    >
                      <NextContentfulImage src={icon.url} alt={name} fill />
                    </button>
                    <LinkCopiedBadge show={copied} isRightAligned={!isMobile} />
                  </>
                ) : (
                  <a
                    aria-label={`${name} - Opens a new window`}
                    href={replace(shareLink)}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.icon}
                  >
                    <NextContentfulImage src={icon.url} alt={name} fill />
                  </a>
                )}
              </li>
            );
          }
        )}
      </ul>
    </div>
  );
};

export default SocialSharing;
